import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell({ user }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count/${user.id}`, {
        params: { team_id: user.team_id }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // silent
    }
  }, [user.id, user.team_id]);

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/user/${user.id}`, {
        params: { team_id: user.team_id, limit: 15 }
      });
      setNotifications(response.data.notifications || response.data || []);
    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 45000);
    return () => clearInterval(interval);
  }, [loadUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) loadNotifications();
  };

  const markRead = async (notification) => {
    if ((notification.read_by || []).includes(user.id)) return;
    try {
      await axios.post(`${API}/notifications/mark-read/${notification.id}`, null, {
        params: { user_id: user.id }
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read_by: [...(n.read_by || []), user.id] } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (error) {
      // silent
    }
  };

  const markAllRead = async () => {
    const unread = notifications.filter((n) => !(n.read_by || []).includes(user.id));
    await Promise.all(
      unread.map((n) =>
        axios.post(`${API}/notifications/mark-read/${n.id}`, null, { params: { user_id: user.id } }).catch(() => {})
      )
    );
    setNotifications((prev) => prev.map((n) => ({ ...n, read_by: [...(n.read_by || []), user.id] })));
    setUnreadCount(0);
  };

  const formatTime = (iso) => {
    try {
      const diff = Date.now() - new Date(iso).getTime();
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return `${Math.floor(hrs / 24)}d ago`;
    } catch {
      return '';
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggleOpen}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-white/80" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 w-80 bg-white text-foreground rounded-lg shadow-xl border z-50 overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/40">
            <span className="font-semibold text-sm">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-primary flex items-center gap-1 hover:underline"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-notifications">
                No notifications yet
              </p>
            ) : (
              notifications.map((n) => {
                const isRead = (n.read_by || []).includes(user.id);
                return (
                  <button
                    key={n.id}
                    onClick={() => markRead(n)}
                    className={`w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${
                      isRead ? 'opacity-60' : 'bg-blue-50/50'
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!isRead && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                      <div className={isRead ? 'pl-4' : ''}>
                        <p className="text-sm font-medium leading-tight">{n.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{formatTime(n.created_at)}</p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
